import { Fragment, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Sidebar from "../components/Sidebar";
import { auth } from "../firebase";
import {
  canEditPpe, canReopenPpe, canViewPpe, createEntry, listEntries, listLockedMonths, listSettlements,
  pullFundsFromInvoices, rejectEntry, reopenPeriod, restoreEntry, settlePeriod, updateEntry
} from "../services/gsolarPpeService";
import {
  ENTRY_KINDS, PPE_COMPANIES, PPE_RATE_PER_EMPLOYEE, PURCHASE_STATUSES, describeBalance, yearOf,
  yearOptions, isActiveEntry, ledgerWithRunningBalance, round, runningPosition, summarizePeriod
} from "../services/ppeCalculator";

const money = (value) => `PKR ${round(value).toLocaleString("en-PK")}`;
const signedMoney = (value) => (round(value) < 0 ? `(${money(Math.abs(value))})` : money(value));
const monthLabel = (period) => {
  const [year, month] = String(period || "").split("-").map(Number);
  if (!year || !month) return period || "";
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
};
const dateLabel = (entry) => {
  if (entry.entryDate) return new Date(`${entry.entryDate}T00:00:00Z`).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
  return `${monthLabel(entry.period)}${entry.dateEstimated ? " (date n/a)" : ""}`;
};

function defaultMonth(start, end) {
  const today = new Date().toISOString().slice(0, 7);
  if (today >= start && today <= end) return today;
  return today > end ? end : start;
}

function emptyForm(kind, month) {
  return {
    kind, period: month, entryDate: "", description: "", amount: "", headCount: "",
    sentTo: "", employeeName: "", supplier: "", billNumber: "", requestRef: "",
    purchaseStatus: "PURCHASED", note: "", dateEstimated: false
  };
}

const thisYear = new Date().getFullYear();
const PERIOD_OPTIONS = yearOptions(2025, thisYear + 1);

export default function GSolarPPE() {
  const editor = canEditPpe();
  const company = PPE_COMPANIES[0].code;
  const [periodKey, setPeriodKey] = useState(() => yearOf(new Date().toISOString().slice(0, 7)).key);
  const [entries, setEntries] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [lockedMonths, setLockedMonths] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [settleForm, setSettleForm] = useState({ amount: "", settledOn: new Date().toISOString().slice(0, 10), reference: "", note: "" });

  const period = PERIOD_OPTIONS.find((option) => option.key === periodKey) || PERIOD_OPTIONS[0];
  const companyLabel = PPE_COMPANIES.find((item) => item.code === company)?.label || company;

  const load = async () => {
    setLoading(true); setError("");
    try {
      const [entryRows, settlementRows, locks] = await Promise.all([listEntries(company), listSettlements(company), listLockedMonths(company)]);
      setEntries(entryRows); setSettlements(settlementRows); setLockedMonths(locks);
    } catch (err) {
      setError(err.message || "Could not load PPE records.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const summary = useMemo(
    () => summarizePeriod({ entries, settlements, start: period.start, end: period.end }),
    [entries, settlements, period]
  );
  const ledger = useMemo(
    () => ledgerWithRunningBalance({ entries, start: period.start, end: period.end, opening: summary.opening }),
    [entries, period, summary.opening]
  );
  const visibleLedger = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return ledger;
    return ledger.filter((entry) => [entry.description, entry.sentTo, entry.employeeName, entry.supplier, entry.billNumber, entry.requestRef, entry.note]
      .some((value) => String(value || "").toLowerCase().includes(query)));
  }, [ledger, search]);

  const position = useMemo(() => runningPosition({ entries, settlements }), [entries, settlements]);
  const hasForward = summary.forward.funds > 0 || summary.forward.purchases > 0 || summary.forward.settled !== 0;
  const direction = describeBalance(summary.closing, companyLabel);
  const settled = Boolean(summary.settlement);

  useEffect(() => {
    setSettleForm((current) => ({ ...current, amount: String(summary.closing) }));
  }, [summary.closing, periodKey]);

  const startForm = (kind) => {
    setEditingId(null); setError(""); setMessage("");
    setForm(emptyForm(kind, defaultMonth(period.start, period.end)));
  };

  const startEdit = (entry) => {
    setEditingId(entry.id); setError(""); setMessage("");
    setForm({
      kind: entry.kind, period: entry.period, entryDate: entry.entryDate || "", description: entry.description || "",
      amount: String(entry.amount ?? ""), headCount: entry.headCount ?? "", sentTo: entry.sentTo || "",
      employeeName: entry.employeeName || "", supplier: entry.supplier || "", billNumber: entry.billNumber || "",
      requestRef: entry.requestRef || "", purchaseStatus: entry.purchaseStatus || "PURCHASED", note: entry.note || "",
      dateEstimated: Boolean(entry.dateEstimated)
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const set = (field) => (e) => setForm((current) => ({ ...current, [field]: e.target.value }));

  const changeDate = (e) => {
    const value = e.target.value;
    setForm((current) => ({ ...current, entryDate: value, period: value ? value.slice(0, 7) : current.period }));
  };

  const changeHeadCount = (e) => {
    const value = e.target.value;
    setForm((current) => ({
      ...current, headCount: value,
      amount: value !== "" ? String(round(Number(value) * PPE_RATE_PER_EMPLOYEE)) : current.amount
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError(""); setMessage("");
    try {
      const payload = { ...form, company };
      if (editingId) await updateEntry(editingId, payload);
      else await createEntry(payload);
      const outside = form.period < period.start || form.period > period.end;
      setMessage(`${form.kind === ENTRY_KINDS.FUND ? "Funds entry" : "Purchase"} ${editingId ? "updated" : "recorded"}.${outside ? ` It belongs to ${monthLabel(form.period)}, outside the period shown — switch period to see it.` : ""}`);
      setForm(null); setEditingId(null);
      await load();
    } catch (err) {
      setError(err.message || "Could not save this entry.");
    }
  };

  const reject = async (entry) => {
    const reason = window.prompt(`Reason for rejecting "${entry.description || "this entry"}" (${money(entry.amount)}). It stays on record but stops counting.`);
    if (reason === null) return;
    setError(""); setMessage("");
    try { await rejectEntry(entry.id, reason); setMessage("Entry rejected."); await load(); }
    catch (err) { setError(err.message || "Could not reject this entry."); }
  };

  const restore = async (entry) => {
    setError(""); setMessage("");
    try { await restoreEntry(entry.id); setMessage("Entry restored."); await load(); }
    catch (err) { setError(err.message || "Could not restore this entry."); }
  };

  const pullFunds = async () => {
    setError(""); setMessage(""); setLoading(true);
    try {
      const added = await pullFundsFromInvoices(company);
      setMessage(added.length
        ? `Added funds for ${added.map((item) => `${monthLabel(item.period)} (${money(item.amount)})`).join(", ")} from approved invoices.`
        : "No new months to add — every approved invoice month already has a funds entry.");
      await load();
    } catch (err) {
      setError(err.message || "Could not read approved invoices.");
      setLoading(false);
    }
  };

  const settle = async (event) => {
    event.preventDefault();
    setError(""); setMessage("");
    const amount = round(settleForm.amount);
    const ok = window.confirm(
      `Settle ${period.label} for ${companyLabel}?\n\nBalance: ${signedMoney(summary.closing)} (${direction.text})\nSettling now: ${signedMoney(amount)}\nCarried forward: ${signedMoney(summary.closing - amount)}\n\nThis locks every month in the period against further changes.`
    );
    if (!ok) return;
    try {
      await settlePeriod({ company, start: period.start, end: period.end, settledAmount: amount, settledOn: settleForm.settledOn, reference: settleForm.reference, note: settleForm.note });
      setMessage(`${period.label} settled and locked.`);
      await load();
    } catch (err) {
      setError(err.message || "Could not settle this period.");
    }
  };

  const reopen = async () => {
    if (!window.confirm(`Reopen ${period.label}? The settlement record is deleted and the months can be edited again.`)) return;
    setError(""); setMessage("");
    try { await reopenPeriod(summary.settlement); setMessage("Period reopened."); await load(); }
    catch (err) { setError(err.message || "Could not reopen this period."); }
  };

  const exportExcel = () => {
    const rows = [
      [`PPE & Material Fund Statement — ${companyLabel}`], [`Period: ${period.label}`], [],
      ["Date / Month", "Type", "Description", "Sent to", "Employee", "Supplier", "Bill no.", "Client request", "Status", "Received", "Purchased", "Balance"],
      ...ledger.filter(isActiveEntry).map((entry) => [
        entry.entryDate || entry.period, entry.kind === ENTRY_KINDS.FUND ? "Received" : "Purchase", entry.description, entry.sentTo,
        entry.employeeName, entry.supplier, entry.billNumber, entry.requestRef, entry.purchaseStatus || "",
        entry.kind === ENTRY_KINDS.FUND ? entry.amount : "", entry.kind === ENTRY_KINDS.PURCHASE ? entry.amount : "", entry.runningBalance
      ]),
      [], ["", "", "", "", "", "", "", "", "Brought forward (unsettled)", summary.forward.funds, summary.forward.purchases, summary.opening],
      ["", "", "", "", "", "", "", "", "Funds received", summary.funds],
      ["", "", "", "", "", "", "", "", "Material purchased", "", summary.purchases],
      ["", "", "", "", "", "", "", "", "Closing balance", "", "", summary.closing]
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(rows), "PPE Statement");
    saveAs(new Blob([XLSX.write(workbook, { bookType: "xlsx", type: "array" })], { type: "application/octet-stream" }), `PPE_Statement_${company}_${period.key}.xlsx`);
  };

  if (!canViewPpe()) {
    return (
      <div className="app-shell"><Sidebar />
        <main className="main-content"><div className="management-alert error">You do not have access to this page.</div></main>
      </div>
    );
  }

  const activeCount = ledger.filter(isActiveEntry).length;
  const now = new Date();

  return (
    <div className="app-shell">
      <Sidebar />
      <main className="main-content">
        <header className="page-header">
          <div>
            <div className="eyebrow">Client Funds</div>
            <h1 className="page-title">GSolar PPE Details</h1>
            <p className="page-copy">
              PPE &amp; material fund: PKR {PPE_RATE_PER_EMPLOYEE} per employee per month is charged to the client, material is bought from it,
              and the balance is reconciled and settled once a year (January to December). Anything not settled rolls into the next year.
            </p>
          </div>
          <div className="pnl-print-hide" style={{ display: "flex", gap: 12 }}>
            <div className="field" style={{ minWidth: 170 }}>
              <label>Period</label>
              <select className="input" value={periodKey} onChange={(e) => setPeriodKey(e.target.value)}>
                {PERIOD_OPTIONS.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
              </select>
            </div>
          </div>
        </header>

        {(message || error) && <div className={`management-alert pnl-print-hide ${error ? "error" : "success"}`}>{error || message}</div>}

        <section className="management-metrics pnl-print-hide">
          <article className="metric-card"><div className="metric-label">Brought Forward</div><div className="metric-value">{signedMoney(summary.opening)}</div>{hasForward && <div className="page-copy" style={{ fontSize: 12 }}>Received {money(summary.forward.funds)} − purchased {money(summary.forward.purchases)}{summary.forward.settled ? ` − settled ${money(summary.forward.settled)}` : ""}</div>}</article>
          <article className="metric-card"><div className="metric-label">Funds Received</div><div className="metric-value">{money(summary.funds)}</div></article>
          <article className="metric-card"><div className="metric-label">Material Purchased</div><div className="metric-value">{money(summary.purchases)}</div></article>
          <article className="metric-card">
            <div className="metric-label">Balance — {direction.text}</div>
            <div className="metric-value">{signedMoney(summary.closing)}</div>
            <span className={`status-pill ${settled ? "success" : "warning"}`} style={{ marginTop: 8 }}>{settled ? "Settled & locked" : "Open"}</span>
          </article>
        </section>

        {editor && (
          <section className="panel pnl-print-hide">
            <div className="panel-header">
              <h2 className="panel-title">{editingId ? "Edit Entry" : "Add Entry"}</h2>
              {!form && (
                <div className="button-row">
                  <button type="button" className="btn btn-primary" onClick={() => startForm(ENTRY_KINDS.PURCHASE)}>Record a Purchase</button>
                  <button type="button" className="btn btn-muted" onClick={() => startForm(ENTRY_KINDS.FUND)}>Record Funds Received</button>
                  <button type="button" className="btn btn-muted" disabled={loading} onClick={pullFunds}>Pull Funds from Approved Invoices</button>
                </div>
              )}
            </div>
            {form && (
              <form className="panel-body" onSubmit={submit}>
                <div className="form-grid">
                  <div className="field"><label>{form.kind === ENTRY_KINDS.FUND ? "Funds received for month" : "Month"}</label>
                    <input className="input" type="month" value={form.period} onChange={set("period")} />
                  </div>
                  <div className="field"><label>Date (optional)</label>
                    <input className="input" type="date" value={form.entryDate} onChange={changeDate} />
                  </div>
                  {form.kind === ENTRY_KINDS.FUND && (
                    <div className="field"><label>Employees billed (x {PPE_RATE_PER_EMPLOYEE})</label>
                      <input className="input" type="number" min="0" value={form.headCount} onChange={changeHeadCount} />
                    </div>
                  )}
                  <div className="field"><label>Amount (PKR)</label>
                    <input className="input" type="number" min="1" value={form.amount} onChange={set("amount")} />
                  </div>
                  <div className="field field-wide"><label>{form.kind === ENTRY_KINDS.FUND ? "Description" : "What was purchased"}</label>
                    <input className="input" value={form.description} onChange={set("description")}
                      placeholder={form.kind === ENTRY_KINDS.FUND ? "PPE funds received from client" : "e.g. Safety shoes x 4, rubber gloves x 6, 20 ft brush"} />
                  </div>
                  {form.kind === ENTRY_KINDS.PURCHASE && (
                    <>
                      <div className="field"><label>Sent to (site / location)</label>
                        <input className="input" value={form.sentTo} onChange={set("sentTo")} placeholder="e.g. Haripur plant" />
                      </div>
                      <div className="field"><label>Employee name (if applicable)</label>
                        <input className="input" value={form.employeeName} onChange={set("employeeName")} />
                      </div>
                      <div className="field"><label>Supplier / paid to</label>
                        <input className="input" value={form.supplier} onChange={set("supplier")} />
                      </div>
                      <div className="field"><label>Bill / receipt no.</label>
                        <input className="input" value={form.billNumber} onChange={set("billNumber")} />
                      </div>
                      <div className="field"><label>Client request (email date / subject)</label>
                        <input className="input" value={form.requestRef} onChange={set("requestRef")} />
                      </div>
                      <div className="field"><label>Status</label>
                        <select className="input" value={form.purchaseStatus} onChange={set("purchaseStatus")}>
                          {PURCHASE_STATUSES.map((status) => <option key={status} value={status}>{status.charAt(0) + status.slice(1).toLowerCase()}</option>)}
                        </select>
                      </div>
                    </>
                  )}
                  <div className="field field-wide"><label>Note</label>
                    <input className="input" value={form.note} onChange={set("note")} />
                  </div>
                </div>
                <div className="button-row" style={{ marginTop: 16 }}>
                  <button className="btn btn-primary" type="submit">{editingId ? "Save Changes" : "Save Entry"}</button>
                  <button className="btn btn-muted" type="button" onClick={() => { setForm(null); setEditingId(null); }}>Cancel</button>
                </div>
              </form>
            )}
          </section>
        )}

        <section className="panel pnl-print-hide">
          <div className="panel-header">
            <div>
              <h2 className="panel-title">Ledger — {period.label}</h2>
              <p className="page-copy">{activeCount} active entr{activeCount === 1 ? "y" : "ies"}. Rejected entries stay visible but do not count.</p>
            </div>
            <div className="button-row">
              <button type="button" className="btn btn-muted" onClick={exportExcel}>Export Excel</button>
              <button type="button" className="btn btn-muted" onClick={() => window.print()}>Print / Save as PDF</button>
            </div>
          </div>
          <div className="panel-body">
            <div className="field" style={{ maxWidth: 420, marginBottom: 12 }}>
              <label>Search</label>
              <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Item, site, employee, supplier, bill no." />
            </div>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr><th>Date</th><th>Type</th><th>Description</th><th>Sent to</th><th>Employee</th><th>Supplier / Bill</th><th>Status</th><th>Received</th><th>Purchased</th><th>Balance</th>{editor && <th>Actions</th>}</tr>
                </thead>
                <tbody>
                  {hasForward && !search.trim() && (
                    <tr style={{ fontWeight: 700, background: "var(--surface)" }}>
                      <td colSpan={7}>Brought forward from earlier periods (not yet settled)</td>
                      <td>{money(summary.forward.funds)}</td><td>{money(summary.forward.purchases)}</td><td>{signedMoney(summary.opening)}</td>{editor && <td />}
                    </tr>
                  )}
                  {visibleLedger.map((entry) => {
                    const rejected = !isActiveEntry(entry);
                    const locked = lockedMonths.has(entry.period);
                    return (
                      <tr key={entry.id} style={rejected ? { opacity: 0.55, textDecoration: "line-through" } : undefined}>
                        <td>{dateLabel(entry)}</td>
                        <td>{entry.kind === ENTRY_KINDS.FUND ? "Received" : "Purchase"}</td>
                        <td>
                          {entry.description}
                          {entry.requestRef && <div className="page-copy" style={{ fontSize: 12 }}>Request: {entry.requestRef}</div>}
                          {entry.note && <div className="page-copy" style={{ fontSize: 12 }}>{entry.note}</div>}
                          {rejected && <div style={{ fontSize: 12, textDecoration: "none" }}>Rejected: {entry.rejectedReason}</div>}
                        </td>
                        <td>{entry.sentTo || "—"}</td>
                        <td>{entry.employeeName || "—"}</td>
                        <td>{[entry.supplier, entry.billNumber].filter(Boolean).join(" · ") || "—"}</td>
                        <td>{entry.kind === ENTRY_KINDS.PURCHASE ? (entry.purchaseStatus || "—") : (entry.headCount ? `${entry.headCount} emp` : "—")}</td>
                        <td>{entry.kind === ENTRY_KINDS.FUND ? money(entry.amount) : ""}</td>
                        <td>{entry.kind === ENTRY_KINDS.PURCHASE ? money(entry.amount) : ""}</td>
                        <td>{signedMoney(entry.runningBalance)}</td>
                        {editor && (
                          <td>
                            {locked ? <span className="status-pill success">Locked</span> : (
                              <div className="table-actions">
                                {!rejected && <button className="btn btn-muted" type="button" onClick={() => startEdit(entry)}>Edit</button>}
                                {rejected
                                  ? <button className="btn btn-muted" type="button" onClick={() => restore(entry)}>Restore</button>
                                  : <button className="btn btn-danger" type="button" onClick={() => reject(entry)}>Reject</button>}
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {!visibleLedger.length && <tr><td colSpan={editor ? 11 : 10}>{loading ? "Loading..." : "No entries for this period."}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="panel pnl-print-hide">
          <div className="panel-header"><h2 className="panel-title">Reconciliation &amp; Settlement — {period.label}</h2></div>
          <div className="panel-body">
            <table className="data-table" style={{ maxWidth: 560 }}>
              <tbody>
                {hasForward && <tr><td>Funds received brought forward</td><td>{money(summary.forward.funds)}</td></tr>}
                {hasForward && <tr><td>− Material purchased brought forward</td><td>{money(summary.forward.purchases)}</td></tr>}
                {summary.forward.settled !== 0 && <tr><td>− Already settled earlier</td><td>{signedMoney(summary.forward.settled)}</td></tr>}
                <tr style={{ fontWeight: 700 }}><td>Balance brought forward</td><td>{signedMoney(summary.opening)}</td></tr>
                <tr><td>+ PPE funds received from client</td><td>{money(summary.funds)}</td></tr>
                <tr><td>− Material purchased</td><td>{money(summary.purchases)}</td></tr>
                <tr style={{ fontWeight: 700 }}><td>Balance — {direction.text}</td><td>{signedMoney(summary.closing)}</td></tr>
                {settled && <tr><td>Settled on {summary.settlement.settledOn}{summary.settlement.reference ? ` (${summary.settlement.reference})` : ""}</td><td>{signedMoney(summary.settled)}</td></tr>}
                {settled && <tr style={{ fontWeight: 700 }}><td>Carried forward to next period</td><td>{signedMoney(summary.carriedForward)}</td></tr>}
              </tbody>
            </table>
            <p className="page-copy" style={{ marginTop: 8 }}>
              <strong>Running position since {position.lastSettlement ? `the last settlement (${position.lastSettlement.periodEnd})` : (position.firstMonth ? monthLabel(position.firstMonth) : "the start")}:</strong>{" "}
              {position.carried ? `carried forward ${signedMoney(position.carried)}, ` : ""}received {money(position.funds)}, purchased {money(position.purchases)} — balance {signedMoney(position.balance)}.
              Periods that are not settled simply roll on: everything received and purchased stays in the next period&apos;s brought-forward balance.
            </p>
            <p className="page-copy" style={{ marginTop: 8 }}>
              {direction.detail}. A negative balance (in brackets) means ALM spent more than it received and is owed that amount by the client.
            </p>

            {settled && summary.settlement.note && <p className="page-copy">Note: {summary.settlement.note}</p>}
            {settled && canReopenPpe() && <button type="button" className="btn btn-muted" onClick={reopen}>Reopen Period</button>}

            {!settled && editor && (
              <form onSubmit={settle} style={{ marginTop: 16 }}>
                <div className="form-grid">
                  <div className="field"><label>Amount settled now (PKR, same sign as balance)</label>
                    <input className="input" type="number" value={settleForm.amount} onChange={(e) => setSettleForm({ ...settleForm, amount: e.target.value })} />
                  </div>
                  <div className="field"><label>Settlement date</label>
                    <input className="input" type="date" value={settleForm.settledOn} onChange={(e) => setSettleForm({ ...settleForm, settledOn: e.target.value })} />
                  </div>
                  <div className="field"><label>Bank / payment reference</label>
                    <input className="input" value={settleForm.reference} onChange={(e) => setSettleForm({ ...settleForm, reference: e.target.value })} />
                  </div>
                  <div className="field field-wide"><label>Note</label>
                    <input className="input" value={settleForm.note} onChange={(e) => setSettleForm({ ...settleForm, note: e.target.value })} />
                  </div>
                </div>
                <p className="page-copy">Any part of the balance not settled now is carried into the next period. Settling locks every month in {period.label}.</p>
                <button className="btn btn-primary" type="submit">Settle &amp; Lock {period.label}</button>
              </form>
            )}
          </div>
        </section>

        <article className="pnl-report ppe-statement" style={{ marginTop: 24 }}>
          <div className="pnl-letterhead">
            <div className="pnl-letterhead-brand">
              <img src="/alm-logo.png" alt="ALM logo" />
              <div>
                <h2 className="pnl-letterhead-title">ALM Outsourcing Services (Pvt) Ltd.</h2>
                <p className="pnl-letterhead-subtitle">PPE &amp; Material Fund Statement — {companyLabel}</p>
              </div>
            </div>
            <div className="pnl-letterhead-meta">
              <strong>{period.label}</strong><br />
              {activeCount} entr{activeCount === 1 ? "y" : "ies"}<br />
              Generated {now.toLocaleDateString("en-GB")} by {auth.currentUser?.email || "—"}
            </div>
          </div>
          <table className="data-table">
            <thead><tr><th>Date</th><th>Description</th><th>Sent to</th><th>Employee</th><th>Received</th><th>Purchased</th><th>Balance</th></tr></thead>
            <tbody>
              <tr style={{ fontWeight: 700 }}><td colSpan={4}>Brought forward (unsettled)</td><td>{hasForward ? money(summary.forward.funds) : ""}</td><td>{hasForward ? money(summary.forward.purchases) : ""}</td><td>{signedMoney(summary.opening)}</td></tr>
              {ledger.filter(isActiveEntry).map((entry) => (
                <Fragment key={entry.id}>
                  <tr>
                    <td>{dateLabel(entry)}</td>
                    <td>{entry.description}{entry.billNumber ? ` (Bill ${entry.billNumber})` : ""}</td>
                    <td>{entry.sentTo}</td><td>{entry.employeeName}</td>
                    <td>{entry.kind === ENTRY_KINDS.FUND ? money(entry.amount) : ""}</td>
                    <td>{entry.kind === ENTRY_KINDS.PURCHASE ? money(entry.amount) : ""}</td>
                    <td>{signedMoney(entry.runningBalance)}</td>
                  </tr>
                </Fragment>
              ))}
              <tr style={{ fontWeight: 700 }}><td colSpan={4} style={{ textAlign: "right" }}>Totals incl. brought forward</td><td>{money(summary.forward.funds + summary.funds)}</td><td>{money(summary.forward.purchases + summary.purchases)}</td><td /></tr>
            </tbody>
          </table>
          <div className="pnl-total-row" style={{ marginTop: 12 }}><span>Balance — {direction.text}</span><strong>{signedMoney(summary.closing)}</strong></div>
          {settled && <div className="pnl-total-row"><span>Settled {summary.settlement.settledOn} — carried forward</span><strong>{signedMoney(summary.carriedForward)}</strong></div>}
          <div className="pnl-footer pnl-print-hide"><span>A balance in brackets is recoverable by ALM from the client.</span></div>
        </article>
      </main>
    </div>
  );
}
